/**
 * AuthContext
 * Provides authentication state and actions throughout the app
 * Manages user, email verification status, and subscription data
 */

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { authService } from '@/services/authService';
import accountService from '@/services/accountService';
import { useNotifications } from './NotificationContext';
import { logger } from '@/services/logger';
import { setSessionId } from '@/services/apiClient';
import { apiPost } from '@/services/apiClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const { showToast } = useNotifications();

  // Bootstrap user account (create if needed and fetch subscription)
  const bootstrapUser = useCallback(async () => {
    try {
      const data = await accountService.bootstrap();
      if (data.subscription) {
        setSubscription(data.subscription);
      }
    } catch (err) {
      logger.error('Failed to bootstrap user', err);
      // Fallback: try to fetch subscription only
      try {
        const data = await accountService.getSubscription();
        setSubscription(data);
      } catch (subErr) {
        logger.error('Failed to fetch subscription', subErr);
        setSubscription(null);
      }
    }
  }, []);

  // Fetch subscription status from API
  const fetchSubscription = useCallback(async () => {
    try {
      const data = await accountService.getSubscription();
      setSubscription(data);
    } catch (err) {
      logger.error('Failed to fetch subscription', err);
      // Silent fail - subscription may not exist yet
      setSubscription(null);
    }
  }, []);

  // Subscribe to auth state changes
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (u) => {
      setUser(u);
      setError(null);

      if (u) {
        // Set initializing flag to prevent session-expired handler from running
        setIsInitializing(true);

        try {
          // Check if we already have a valid session
          const existingSessionId = localStorage.getItem('sessionId');

          if (existingSessionId) {
            // Try to use existing session
            logger.info('Using existing session', { sessionId: existingSessionId });
            try {
              // Bootstrap user with existing session
              await bootstrapUser();
              logger.info('User bootstrap complete with existing session');
            } catch (err) {
              // If bootstrap fails with session_expired, don't create new session
              // Let the session-expired handler deal with it (force re-login)
              if (err.code === 'session_expired') {
                logger.warn('Session expired - will force re-login');
                // Clear the session and trigger the session-expired event
                setSessionId(null);
                throw err;
              } else {
                throw err;
              }
            }
          } else {
            // No existing session, create a new one
            logger.info('No existing session, creating new one');
            const response = await apiPost('/auth/create-session');
            if (response.sessionId) {
              setSessionId(response.sessionId);
              logger.info('Session created successfully', { sessionId: response.sessionId });
            } else {
              throw new Error('No session ID returned from server');
            }

            // Bootstrap user only AFTER session is created
            logger.info('Bootstrapping user account');
            await bootstrapUser();
            logger.info('User bootstrap complete');
          }
        } catch (err) {
          logger.error('Failed during auth initialization', err);
          logger.error('Error details:', {
            message: err.message,
            code: err.code,
            status: err.status
          });

          // If session creation or bootstrap fails, sign out the user
          // Force Firebase sign out to clear auth state
          await authService.signOut().catch(() => {});
          setUser(null);
          setSessionId(null);
          showToast({
            type: 'error',
            message: `Erreur lors de la connexion: ${err.message}`
          });
        } finally {
          setIsInitializing(false);
        }
      } else {
        setSubscription(null);
        // Clear session ID on logout
        setSessionId(null);
      }

      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [bootstrapUser, showToast]);

  // Handle session-expired event from API client
  useEffect(() => {
    const handleSessionExpired = async (event) => {
      // Don't handle session-expired during initialization (login flow)
      if (isInitializing) {
        logger.debug('Ignoring session-expired during initialization');
        return;
      }

      logger.warn('Session expired - logging out user', event.detail);

      // Show notification to user
      showToast({
        type: 'error',
        message: event.detail.message || 'Votre session a expiré. Veuillez vous reconnecter.',
        duration: 5000
      });

      // Sign out the user (will clear session and redirect)
      try {
        await authService.signOut();
        setUser(null);
        setSubscription(null);
        setSessionId(null);
      } catch (err) {
        logger.error('Failed to sign out on session expiry', err);
      }
    };

    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, [showToast, isInitializing]);

  // Sign up with email and password
  const signUp = useCallback(
    async (email, password, displayName) => {
      try {
        const u = await authService.signUp(email, password, displayName);
        showToast({
          type: 'success',
          message: 'Email de vérification envoyé. Vérifiez votre boîte de réception.'
        });
        // User will be set by onAuthStateChanged, which will also bootstrap
        return u;
      } catch (err) {
        setError(err);
        logger.error('Sign up failed', err);
        throw err;
      }
    },
    [showToast]
  );

  // Sign in with email and password
  const signIn = useCallback(async (email, password) => {
    try {
      const credential = await authService.signIn(email, password);
      // User and subscription will be set by onAuthStateChanged
      return credential;
    } catch (err) {
      setError(err);
      logger.error('Sign in failed', err);
      throw err;
    }
  }, []);

  // Sign out current user
  const signOut = useCallback(async () => {
    try {
      // Invalidate session on the backend
      try {
        await apiPost('/auth/invalidate-session');
      } catch (err) {
        logger.error('Failed to invalidate session', err);
        // Continue with logout even if session invalidation fails
      }

      await authService.signOut();
      setUser(null);
      setSubscription(null);
      // Clear session ID and redirect after logout
      setSessionId(null);
      localStorage.removeItem('auth:redirectAfterLogin');
    } catch (err) {
      logger.error('Sign out failed', err);
      throw err;
    }
  }, []);

  // Send password reset email
  const sendPasswordReset = useCallback(
    async (email) => {
      try {
        await authService.sendPasswordReset(email);
        showToast({
          type: 'success',
          message: 'Lien de réinitialisation envoyé. Vérifiez votre boîte de réception.'
        });
      } catch (err) {
        logger.error('Send password reset failed', err);
        throw err;
      }
    },
    [showToast]
  );

  // Send email verification to current user
  const sendEmailVerification = useCallback(async () => {
    try {
      await authService.sendEmailVerification();
      // Store cooldown timestamp
      const cooldownUntil = Date.now() + 60000; // 60 seconds
      localStorage.setItem('auth:resendVerifyCooldown', cooldownUntil.toString());
      showToast({
        type: 'success',
        message: 'Email de vérification envoyé.'
      });
    } catch (err) {
      logger.error('Send email verification failed', err);
      throw err;
    }
  }, [showToast]);

  // Refresh user to update email verification status
  const refreshUser = useCallback(async () => {
    try {
      const updatedUser = await authService.refreshUser();
      setUser(updatedUser);
      return updatedUser;
    } catch (err) {
      logger.error('Failed to refresh user', err);
      throw err;
    }
  }, []);

  // Verify email using token from Brevo
  const verifyEmail = useCallback(
    async (token) => {
      try {
        await authService.verifyEmail(token);
        // Refresh user to get updated email verification status
        const updatedUser = await refreshUser();
        showToast({
          type: 'success',
          message: 'Email verifie avec succes !'
        });
        return updatedUser;
      } catch (err) {
        logger.error('Failed to verify email', err);
        throw err;
      }
    },
    [refreshUser, showToast]
  );

  // Verify password reset token
  const verifyResetToken = useCallback(async (token) => {
    try {
      return await authService.verifyResetToken(token);
    } catch (err) {
      logger.error('Failed to verify reset token', err);
      throw err;
    }
  }, []);

  // Reset password using token
  const resetPassword = useCallback(
    async (token, password) => {
      try {
        await authService.resetPassword(token, password);
        showToast({
          type: 'success',
          message: 'Mot de passe mis a jour avec succes !'
        });
      } catch (err) {
        logger.error('Failed to reset password', err);
        throw err;
      }
    },
    [showToast]
  );

  // Refresh subscription data (e.g., after Stripe redirect)
  const refreshSubscription = fetchSubscription;

  const value = useMemo(() => {
    const isEmailVerified = !!user?.emailVerified;
    const emailVerified = isEmailVerified; // Alias for consistency with spec
    const now = Date.now();
    const hasActiveSubscription =
      !!subscription &&
      (subscription.status === 'active' ||
        (subscription.currentPeriodEnd && new Date(subscription.currentPeriodEnd).getTime() > now));

    return {
      user,
      isLoading,
      loading: isLoading, // Alias for consistency with spec
      error,
      subscription: subscription || {
        status: 'inactive',
        priceId: null,
        currentPeriodStart: null,
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false
      },
      hasActiveSubscription,
      isEmailVerified,
      emailVerified, // Alias for consistency with spec
      signUp,
      signIn,
      signOut,
      sendPasswordReset,
      sendEmailVerification,
      refreshSubscription,
      refreshUser,
      verifyEmail,
      verifyResetToken,
      resetPassword,
      // Legacy API compatibility
      isSubscribedActive: () => hasActiveSubscription,
      setSubscription,
      role: user?.customClaims?.role || 'host'
    };
  }, [
    user,
    isLoading,
    error,
    subscription,
    signUp,
    signIn,
    signOut,
    sendPasswordReset,
    sendEmailVerification,
    refreshSubscription,
    refreshUser,
    verifyEmail,
    verifyResetToken,
    resetPassword
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export default AuthContext;
